import { DOCUMENT, inject, Service, signal } from '@angular/core';
import { DEFAULT_TEMPLATE, TemplateKey } from './constants';

/** id of the single runtime <link> that carries the active theme file. */
const THEME_LINK_ID = 'qm-theme';
/** Give up on a theme file after this long and fall back to the default look, so a stalled request
    can never leave the menu stuck behind the skeleton. */
const THEME_LOAD_TIMEOUT = 5000;

/**
 * Loads a design-template stylesheet at runtime and reports which template is actually in effect.
 *
 * `default` has no file: applying it removes the <link> (if any) and reports `default`, so the
 * default template costs zero network and zero DOM. For a real theme a <link> is inserted (or
 * replaced) pointing at `public/themes/<key>.css`, and `applied` flips to the new key ONLY after
 * the CSS `load` fires — the host's `[data-template]` and the page's skeleton gate both read this,
 * so the switch is atomic and the guest never sees the default look flash before the theme lands.
 * A load error/timeout marks the key `failed` and degrades to the default look (the gate stops waiting).
 */
@Service()
export class ThemeLoader {
  private doc = inject(DOCUMENT);

  private readonly _applied = signal<TemplateKey>(DEFAULT_TEMPLATE);
  /** Template whose CSS is live right now; stays `default` until a theme's <link> finishes loading. */
  readonly applied = this._applied.asReadonly();

  private readonly _failed = signal<TemplateKey | null>(null);
  /** Template whose CSS failed to load (404 / network / timeout) — consumers treat it as settled
      on the default look. Null while idle or mid-load. */
  readonly failed = this._failed.asReadonly();

  /** Last requested key — guards a stale <link> load/error from resolving after a newer request. */
  private target: TemplateKey = DEFAULT_TEMPLATE;
  private link: HTMLLinkElement | null = null;
  private timer: number | null = null;

  apply(key: TemplateKey): void {
    if (key === this.target) return; // already loading/loaded this one — idempotent, no dup <link>
    this.target = key;
    this.reset();

    if (key === DEFAULT_TEMPLATE) {
      this._applied.set(DEFAULT_TEMPLATE);
      return;
    }

    const link = this.doc.createElement('link');
    link.id = THEME_LINK_ID;
    link.rel = 'stylesheet';
    link.href = `themes/${key}.css`;
    link.addEventListener(
      'load',
      () => {
        // Ignore if a newer apply() superseded this request while the file was in flight.
        if (this.target !== key) return;
        this.clearTimer();
        this._applied.set(key);
      },
      { once: true },
    );
    link.addEventListener('error', () => this.degrade(key), { once: true });
    this.doc.head.appendChild(link);
    this.link = link;
    this.timer = this.doc.defaultView?.setTimeout(() => this.degrade(key), THEME_LOAD_TIMEOUT) ?? null;
  }

  /** Theme file missing/blocked/stalled — mark it failed, drop the dead <link>, stay on default. */
  private degrade(key: TemplateKey): void {
    if (this.target !== key) return;
    this.target = DEFAULT_TEMPLATE;
    this.reset();
    this._applied.set(DEFAULT_TEMPLATE);
    this._failed.set(key);
  }

  /** Cancel any in-flight load: drop the timer, the <link>, and the stale failure marker. */
  private reset(): void {
    this.clearTimer();
    this.removeLink();
    this._failed.set(null);
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      this.doc.defaultView?.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private removeLink(): void {
    this.link?.remove();
    this.link = null;
  }
}
